import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/Layout/DashboardLayout";
import { LayoutDashboard, Package, DollarSign, Wifi, WifiOff } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { useCurrency } from "@/contexts/CurrencyContext";

const items = (t) => [
  { to: "/rider", icon: LayoutDashboard, label: t("dashboard"), end: true, tid: "rider-nav-dashboard" },
  { to: "/rider/jobs", icon: Package, label: t("availableJobs"), tid: "rider-nav-jobs" },
  { to: "/rider/earnings", icon: DollarSign, label: t("earnings"), tid: "rider-nav-earnings" },
];

export default function RiderDashboard() {
  const { user, refreshUser } = useAuth();
  const { t } = useLang();
  const { formatPrice } = useCurrency();
  const [earn, setEarn] = useState({ total_earnings: 0, deliveries_completed: 0 });
  const [orders, setOrders] = useState([]);

  const load = async () => {
    const [e, o] = await Promise.all([api.get("/rider/earnings"), api.get("/orders/mine")]);
    setEarn(e.data); setOrders(o.data);
  };
  useEffect(() => { load(); const i = setInterval(load, 5000); return () => clearInterval(i); }, []);

  const toggle = async () => {
    await api.patch("/rider/status", { online: !user.rider_online });
    await refreshUser();
  };

  const advance = async (id) => { await api.post(`/orders/${id}/advance`); load(); };

  const active = orders.filter(o => o.status !== "delivered");
  const online = user.rider_online;

  return (
    <DashboardLayout items={items(t)} title={t("dashboard")}>
      <div className={`so-card p-6 mb-6 flex items-center justify-between ${online ? "bg-emerald-500/5 border-emerald-500/30" : ""}`} data-testid="rider-status-card">
        <div>
          <div className="flex items-center gap-2">
            <span className={`w-3 h-3 rounded-full ${online ? "bg-emerald-500 so-pulse" : "bg-gray-400"}`} />
            <span className="font-bold text-lg">{online ? t("online") : t("offline")}</span>
          </div>
          <div className="text-sm text-[var(--so-text-soft)] mt-1">{online ? "Receiving job requests" : "You won't receive jobs"}</div>
        </div>
        <button onClick={toggle} data-testid="toggle-online" className={online ? "so-btn-secondary" : "so-btn-primary"}>
          {online ? <><WifiOff className="w-4 h-4" /> {t("goOffline")}</> : <><Wifi className="w-4 h-4" /> {t("goOnline")}</>}
        </button>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <StatCard label={t("earnings")} value={formatPrice(earn.total_earnings)} tint="text-[var(--so-orange)]" tid="r-earnings" />
        <StatCard label={t("deliveries")} value={earn.deliveries_completed} tid="r-deliveries" />
        <StatCard label="Active jobs" value={active.length} tint="text-emerald-600" tid="r-active" />
      </div>

      <div className="so-card p-5">
        <h2 className="font-bold mb-3">Active deliveries</h2>
        {active.length === 0 ? (
          <div className="text-sm text-[var(--so-text-soft)] py-4">No active deliveries.</div>
        ) : (
          <div className="space-y-2">
            {active.map(o => (
              <div key={o.id} className="p-3 rounded-xl bg-[var(--so-bg)] flex items-center gap-3" data-testid={`active-${o.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold">{o.restaurant_name} → {o.customer_name}</div>
                  <div className="text-xs text-[var(--so-text-soft)]">{o.address} · {formatPrice(o.delivery_fee)} fee</div>
                </div>
                <span className="text-[10px] font-bold uppercase px-2 py-1 rounded-full bg-orange-500/10 text-orange-700">{t(o.status)}</span>
                {["picked_up", "out_for_delivery"].includes(o.status) && (
                  <button onClick={() => advance(o.id)} className="so-btn-primary text-xs py-1.5 px-3" data-testid={`r-advance-${o.id}`}>
                    {o.status === "picked_up" ? "Start delivery" : "Mark delivered"}
                  </button>
                )}
                {o.status === "ready" && (
                  <button onClick={() => advance(o.id)} className="so-btn-primary text-xs py-1.5 px-3" data-testid={`pickup-${o.id}`}>
                    Pick up
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

const StatCard = ({ label, value, tint, tid }) => (
  <div className="so-card p-4" data-testid={tid}>
    <div className="text-xs uppercase tracking-wider text-[var(--so-text-soft)] font-bold">{label}</div>
    <div className={`text-3xl font-extrabold mt-1 ${tint || ""}`}>{value}</div>
  </div>
);
